<template>
  <div v-if="state?.active" ref="root" class="cop" :class="`is-${stage}`">
    <!-- Barra: o agente de IA "vivo" (lendo, escrevendo, pronta), o envio automático e o dia do atendente -->
    <div class="cop-row">
      <div class="cop-who" :title="`O agente de IA ${state.agentName} sugere as respostas neste Fluxo`">
        <span class="cop-av" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="7" width="16" height="12" rx="3" /><path d="M12 3v4M9 12h.01M15 12h.01M9 16h6" /></svg>
          <span v-if="stage === 'ready'" class="cop-av-check">✓</span>
        </span>
        <span class="cop-who-text">
          <b>{{ state.agentName }}</b>
          <span class="cop-stage" aria-live="polite">
            {{ STAGE_TEXT[stage] }}
            <span v-if="stage === 'writing'" class="cop-dots" aria-hidden="true"><i></i><i></i><i></i></span>
          </span>
        </span>
      </div>

      <label class="cop-switch" :title="AUTO_HINT">
        <input id="cop-auto" type="checkbox" role="switch" :checked="state.autoSend" :disabled="savingAuto" @change="toggleAuto(($event.target as HTMLInputElement).checked)" />
        <span class="cop-switch-track" aria-hidden="true"><span class="cop-switch-thumb"></span></span>
        <span class="cop-switch-text">Enviar sozinho</span>
      </label>

      <span v-if="today" class="cop-today" :title="`Hoje: ${today.used} usadas como vieram, ${today.edited} editadas, ${today.discarded} descartadas`">
        Hoje: <b>{{ today.used + today.edited }}</b> aproveitada(s)
      </span>
      <transition name="cop-pop">
        <span v-if="milestone" class="cop-milestone" role="status">✦ {{ milestone }}</span>
      </transition>

      <button type="button" class="cop-link" :disabled="asking" @click="askAgain">{{ asking ? 'Pedindo…' : 'Sugerir de novo' }}</button>
    </div>

    <!-- Cartão da sugestão -->
    <transition name="cop-card">
      <div v-if="suggestion" :class="['cop-card', `cop-card--${confidence}`]">
        <div class="cop-card-head">
          <span :class="['cop-conf', `cop-conf--${confidence}`]" :title="CONF_HINT[confidence]">
            <span class="cop-conf-bars" aria-hidden="true"><i></i><i></i><i></i></span>
            Confiança {{ CONF_LABEL[confidence] }}
          </span>
          <span v-if="suggestion.sourceTitle" class="cop-source" :title="suggestion.reason ?? ''">Baseado em: <b>{{ suggestion.sourceTitle }}</b></span>
          <span v-else class="cop-source cop-source--none">{{ suggestion.reason || 'Sem base na dúvida frequente nem em documento: revise antes de enviar.' }}</span>
          <span v-if="suggestion.tone" class="cop-tone-tag">tom: {{ TONE_LABEL[suggestion.tone] }}</span>
        </div>

        <p v-if="!inDraft" class="cop-text">{{ typed }}<span v-if="typing" class="cop-caret" aria-hidden="true"></span></p>
        <p v-else class="cop-hint">Já está no campo de mensagem — edite se quiser e envie.</p>

        <div class="cop-actions">
          <template v-if="!inDraft">
            <button type="button" class="cop-btn cop-btn--primary" @click="use">Usar <kbd>Tab</kbd></button>
            <button type="button" class="cop-btn" @click="edit">Editar</button>
          </template>
          <button type="button" class="cop-btn cop-btn--ghost" @click="discard">Descartar <kbd>Esc</kbd></button>

          <span class="cop-sep" aria-hidden="true"></span>
          <div class="cop-tones" role="group" aria-label="Reescrever noutro tom">
            <button v-for="t in TONES" :key="t.value" type="button" :class="['cop-chip', { 'is-on': suggestion.tone === t.value }]"
              :disabled="!!rewriting" :title="`${t.hint} — gasta alguns tokens`" @click="rewrite(t.value)">
              <span v-if="rewriting === t.value" class="cop-spin" aria-hidden="true"></span>{{ t.label }}
            </button>
          </div>

          <div class="cop-rate" role="group" aria-label="Avaliar a sugestão">
            <button type="button" :class="['cop-thumb', { 'is-on': rated === true }]" :aria-pressed="rated === true" title="Boa sugestão" @click="rate(true)">👍</button>
            <button type="button" :class="['cop-thumb', { 'is-on': rated === false }]" :aria-pressed="rated === false" title="Sugestão ruim" @click="rate(false)">👎</button>
          </div>
        </div>
        <p v-if="rewriteProblem" class="cop-problem" role="alert">{{ rewriteProblem }}</p>
      </div>
    </transition>
    <p v-if="!suggestion && autoNote" class="cop-hint cop-auto-note">{{ autoNote }}</p>
  </div>
</template>

<script setup lang="ts">
// Copiloto de IA no chat do atendente. A sugestão chega por SignalR (SugestaoCopiloto) e o que o agente de IA está fazendo
// (CopilotoStatus: lendo, escrevendo, sem sugestão). Com o campo vazio a sugestão já entra no campo; com texto digitado,
// fica no cartão pra "Usar" (Tab) ou "Descartar" (Esc). O pai avisa o envio (exposto: sent) pra medir usada/editada/descartada.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { onCopilotStatus, onCopilotSuggestion, type CopilotStatusNotification, type CopilotSuggestionNotification } from '@/composables/useConversationRealtime'
import {
  decideCopilotSuggestion, decisionFor, getCopilotState, rateCopilotSuggestion, requestCopilotSuggestion, rewriteCopilotSuggestion, setCopilotAutoSend,
  type CopilotDecision, type CopilotState, type CopilotTone,
} from '@/services/wootdashCopilot'

const props = defineProps<{ conversationId: string; draft: string }>()
const emit = defineEmits<{ fill: [text: string] }>()

type Stage = 'idle' | 'reading' | 'writing' | 'ready' | 'none'
type Confidence = 'alta' | 'media' | 'baixa'

const STAGE_TEXT: Record<Stage, string> = {
  idle: 'pronto pra ajudar',
  reading: 'lendo a conversa…',
  writing: 'escrevendo uma sugestão',
  ready: 'sugestão pronta',
  none: 'sem sugestão pra esta mensagem',
}
const CONF_LABEL: Record<Confidence, string> = { alta: 'alta', media: 'média', baixa: 'baixa' }
const CONF_HINT: Record<Confidence, string> = {
  alta: 'Muito parecida com uma dúvida frequente revisada ou um documento da base.',
  media: 'Parecida com uma dúvida frequente ou documento — vale uma conferida.',
  baixa: 'Sem base suficiente (ou o agente quis transferir) — revise antes de enviar.',
}
const TONES: { value: CopilotTone; label: string; hint: string }[] = [
  { value: 'curta', label: 'Mais curta', hint: 'A mesma resposta, mais direta' },
  { value: 'formal', label: 'Mais formal', hint: 'A mesma resposta, mais profissional' },
  { value: 'acolhedora', label: 'Mais acolhedora', hint: 'A mesma resposta, mais simpática' },
]
const TONE_LABEL: Record<CopilotTone, string> = { curta: 'mais curta', formal: 'mais formal', acolhedora: 'mais acolhedora' }
const AUTO_HINT = 'Ligado, a sugestão sai sozinha em seu nome, marcada como copiloto (menos quando o agente quer transferir ou uma ferramenta falhou).'
const MILESTONES = [10, 25, 50, 100]

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')

const root = ref<HTMLElement | null>(null)
const state = ref<CopilotState | null>(null)
const suggestion = ref<CopilotSuggestionNotification | null>(null)
const stage = ref<Stage>('idle')
const savingAuto = ref(false)
const asking = ref(false)
const autoNote = ref<string | null>(null)
const rewriting = ref<CopilotTone | null>(null)
const rewriteProblem = ref<string | null>(null)
const rated = ref<boolean | null>(null)
const milestone = ref<string | null>(null)
const typed = ref('')
const typing = ref(false)
let unsubscribe: (() => void)[] = []
let stageTimer: ReturnType<typeof setTimeout> | null = null
let typeTimer: ReturnType<typeof setInterval> | null = null
let milestoneTimer: ReturnType<typeof setTimeout> | null = null

const today = computed(() => state.value?.today ?? null)
const confidence = computed<Confidence>(() => suggestion.value?.confidence ?? (suggestion.value?.safe ? 'media' : 'baixa'))
/** A sugestão está no campo (do jeito que veio ou editada a partir dela). */
const inDraft = computed(() => !!suggestion.value && props.draft.trim().length > 0 && decisionFor(suggestion.value.text, props.draft) !== 'discarded')
const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function setStage(next: Stage) {
  stage.value = next
  if (stageTimer) clearTimeout(stageTimer)
  // "Pronta"/"sem sugestão" voltam a "pronto pra ajudar" depois de um tempo; "escrevendo" sem resposta também.
  if (next === 'none' || next === 'ready') stageTimer = setTimeout(() => { stage.value = 'idle' }, next === 'none' ? 5000 : 8000)
  if (next === 'reading' || next === 'writing') stageTimer = setTimeout(() => { stage.value = 'idle' }, 60000)
}

/** Efeito de digitação curto (~0,7 s, qualquer tamanho) — some com "reduzir movimento". */
function typeOut(text: string) {
  if (typeTimer) clearInterval(typeTimer)
  if (reducedMotion) { typed.value = text; typing.value = false; return }
  const step = Math.max(2, Math.ceil(text.length / 35))
  let i = 0
  typing.value = true
  typed.value = ''
  typeTimer = setInterval(() => {
    i = Math.min(text.length, i + step)
    typed.value = text.slice(0, i)
    if (i >= text.length) { clearInterval(typeTimer!); typeTimer = null; typing.value = false }
  }, 20)
}

function showSuggestion(evt: CopilotSuggestionNotification) {
  suggestion.value = evt
  rated.value = null
  rewriteProblem.value = null
  typeOut(evt.text)
}

function receive(evt: CopilotSuggestionNotification) {
  asking.value = false
  if (evt.status === 'auto_sent') {
    suggestion.value = null
    setStage('ready')
    autoNote.value = `O copiloto respondeu sozinho às ${new Date(evt.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}.`
    return
  }
  autoNote.value = null
  setStage('ready')
  showSuggestion(evt)
  if (!props.draft.trim()) emit('fill', evt.text)
}

function onStatus(evt: CopilotStatusNotification) {
  if (evt.stage === 'none') { asking.value = false; setStage('none') }
  else setStage(evt.stage)
}

async function load(conversationId: string) {
  unsubscribe.forEach(u => u())
  unsubscribe = []
  suggestion.value = null
  autoNote.value = null
  stage.value = 'idle'
  state.value = await getCopilotState(token.value, conversationId)
  if (!state.value?.active) return
  unsubscribe = [onCopilotSuggestion(conversationId, receive), onCopilotStatus(conversationId, onStatus)]
  if (state.value.pending) showSuggestion(state.value.pending)
}

async function toggleAuto(on: boolean) {
  if (!state.value) return
  savingAuto.value = true
  const ok = await setCopilotAutoSend(token.value, props.conversationId, on)
  savingAuto.value = false
  if (ok) state.value = { ...state.value, autoSend: on }
}

async function askAgain() {
  asking.value = true
  if (!(await requestCopilotSuggestion(token.value, props.conversationId))) { asking.value = false; return }
  setStage('reading')
  // A resposta chega pelo SignalR (receive/onStatus); sem mensagem do contato, nada chega.
  setTimeout(() => { asking.value = false }, 30000)
}

function composer(): HTMLTextAreaElement | HTMLInputElement | null {
  return root.value?.parentElement?.querySelector('textarea, input[type="text"]:not([readonly])') ?? null
}

function use() {
  if (suggestion.value) emit('fill', suggestion.value.text)
}

function edit() {
  use()
  setTimeout(() => { const el = composer(); el?.focus(); if (el) el.selectionStart = el.selectionEnd = el.value.length }, 30)
}

function countDecision(decision: CopilotDecision) {
  const t = state.value?.today
  if (!state.value || !t) return
  const next = { ...t, [decision]: t[decision] + 1 }
  state.value = { ...state.value, today: next }
  const before = t.used + t.edited
  const after = next.used + next.edited
  const hit = MILESTONES.find(m => before < m && after >= m)
  if (hit) celebrate(`${hit} sugestões aproveitadas hoje`)
}

function celebrate(text: string) {
  milestone.value = text
  if (milestoneTimer) clearTimeout(milestoneTimer)
  milestoneTimer = setTimeout(() => { milestone.value = null }, 4500)
}

function discard() {
  if (!suggestion.value) return
  const current = suggestion.value
  decideCopilotSuggestion(token.value, current.suggestionId, 'discarded')
  countDecision('discarded')
  if (props.draft.trim() === current.text.trim()) emit('fill', '')
  suggestion.value = null
}

async function rewrite(tone: CopilotTone) {
  if (!suggestion.value) return
  const current = suggestion.value
  rewriting.value = tone
  rewriteProblem.value = null
  const result = await rewriteCopilotSuggestion(token.value, current.suggestionId, tone)
  rewriting.value = null
  if (suggestion.value?.suggestionId !== current.suggestionId) return
  if (!result.success || !result.text) { rewriteProblem.value = result.problem ?? 'Não deu pra reescrever agora.'; return }
  const wasInField = props.draft.trim() === current.text.trim() || !props.draft.trim()
  suggestion.value = { ...current, text: result.text, tone: result.tone }
  typeOut(result.text)
  if (wasInField) emit('fill', result.text)
}

function rate(helpful: boolean) {
  if (!suggestion.value) return
  rated.value = helpful
  rateCopilotSuggestion(token.value, suggestion.value.suggestionId, helpful)
  if (state.value?.today && rated.value !== null) {
    const t = state.value.today
    state.value = { ...state.value, today: { ...t, helpful: t.helpful + (helpful ? 1 : 0), notHelpful: t.notHelpful + (helpful ? 0 : 1) } }
  }
}

/** O pai chama depois de enviar uma mensagem (não nota interna). */
function sent(text: string) {
  if (!suggestion.value) return
  const decision = decisionFor(suggestion.value.text, text)
  decideCopilotSuggestion(token.value, suggestion.value.suggestionId, decision)
  countDecision(decision)
  suggestion.value = null
}

defineExpose({ sent })

/** Tab = usar, Esc = descartar — só com o foco no campo de mensagem deste chat e o cartão aberto. */
function onKey(e: KeyboardEvent) {
  if (!suggestion.value || e.altKey || e.ctrlKey || e.metaKey) return
  const target = e.target as HTMLElement | null
  if (!target || !root.value?.parentElement?.contains(target) || root.value.contains(target)) return
  if (!(target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement)) return
  if (e.key === 'Tab' && !e.shiftKey && !inDraft.value && !props.draft.trim()) { e.preventDefault(); use() }
  else if (e.key === 'Escape') { e.preventDefault(); discard() }
}

watch(() => props.conversationId, id => { if (id) load(id) }, { immediate: true })
onMounted(() => document.addEventListener('keydown', onKey, true))
onBeforeUnmount(() => {
  unsubscribe.forEach(u => u())
  document.removeEventListener('keydown', onKey, true)
  for (const t of [stageTimer, milestoneTimer]) if (t) clearTimeout(t)
  if (typeTimer) clearInterval(typeTimer)
})
</script>

<style scoped>
/* Roxo do copiloto (#7c3aed) — mesma cor de sempre; o resto vem das variáveis do painel. */
.cop { --cop: #7c3aed; --cop-soft: rgba(124,58,237,.1); display: flex; flex-direction: column; gap: 8px; padding: 8px 12px; border-top: 1px solid var(--ink-05, rgba(0,0,0,.06)); background: var(--ink-02, #fafafa); }
.cop-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-width: 0; }

/* Quem é e o que está fazendo */
.cop-who { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
.cop-av {
  position: relative; width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center; flex: none;
  color: var(--cop); background: var(--cop-soft); box-shadow: 0 0 0 0 rgba(124,58,237,.35); transition: background .2s, color .2s;
}
.is-reading .cop-av { animation: cop-pulse 1.4s ease-out infinite; }
.is-writing .cop-av { background: var(--cop); color: #fff; }
.is-ready .cop-av { background: #16a34a; color: #fff; }
.cop-av-check { position: absolute; right: -3px; bottom: -3px; width: 13px; height: 13px; border-radius: 50%; background: #16a34a; color: #fff; font-size: 8px; font-weight: 800; display: grid; place-items: center; border: 2px solid var(--ink-02, #fafafa); }
.cop-who-text { display: flex; flex-direction: column; line-height: 1.15; min-width: 0; }
.cop-who-text b { font-size: 12px; color: var(--cop); }
.cop-stage { font-size: 11px; color: var(--ink-50, #666); display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
.is-none .cop-stage { color: var(--ink-40, #888); font-style: italic; }
.is-ready .cop-stage { color: #15803d; font-weight: 600; }
.cop-dots { display: inline-flex; gap: 2px; }
.cop-dots i { width: 4px; height: 4px; border-radius: 50%; background: var(--cop); animation: cop-dot 1s infinite ease-in-out; }
.cop-dots i:nth-child(2) { animation-delay: .15s; }
.cop-dots i:nth-child(3) { animation-delay: .3s; }

/* Chave "enviar sozinho" */
.cop-switch { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ink-60, #555); cursor: pointer; user-select: none; }
.cop-switch input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.cop-switch-track { width: 30px; height: 17px; border-radius: 999px; background: var(--ink-10, #ddd); position: relative; transition: background .18s; flex: none; }
.cop-switch-thumb { position: absolute; top: 2px; left: 2px; width: 13px; height: 13px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: transform .18s; }
.cop-switch input:checked + .cop-switch-track { background: var(--cop); }
.cop-switch input:checked + .cop-switch-track .cop-switch-thumb { transform: translateX(13px); }
.cop-switch input:focus-visible + .cop-switch-track { outline: 2px solid var(--cop); outline-offset: 2px; }
.cop-switch input:disabled + .cop-switch-track { opacity: .5; }

.cop-today { font-size: 11px; color: var(--ink-50, #666); font-variant-numeric: tabular-nums; }
.cop-today b { color: var(--ink-80, #222); }
.cop-milestone { font-size: 11px; font-weight: 700; color: var(--cop); background: var(--cop-soft); padding: 2px 9px; border-radius: 999px; }
.cop-link { margin-left: auto; border: none; background: transparent; color: var(--cop); font: 600 12px/1 inherit; cursor: pointer; padding: 2px 0; }
.cop-link:disabled { opacity: .5; cursor: default; }

/* Cartão da sugestão */
.cop-card { display: flex; flex-direction: column; gap: 7px; padding: 9px 11px; border-radius: 10px; background: var(--white, #fff); border: 1px solid var(--ink-10, #e5e5e5); border-left: 3px solid; box-shadow: 0 1px 3px rgba(0,0,0,.04); }
.cop-card--alta { border-left-color: #16a34a; }
.cop-card--media { border-left-color: #d97706; }
.cop-card--baixa { border-left-color: #dc2626; }
.cop-card-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 11.5px; min-width: 0; }
.cop-conf { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 999px; font-weight: 700; font-size: 11px; }
.cop-conf--alta { background: rgba(22,163,74,.1); color: #15803d; }
.cop-conf--media { background: rgba(217,119,6,.12); color: #b45309; }
.cop-conf--baixa { background: rgba(220,38,38,.1); color: #b91c1c; }
.cop-conf-bars { display: inline-flex; align-items: flex-end; gap: 1.5px; height: 10px; }
.cop-conf-bars i { width: 3px; border-radius: 1px; background: currentColor; opacity: .25; }
.cop-conf-bars i:nth-child(1) { height: 4px; } .cop-conf-bars i:nth-child(2) { height: 7px; } .cop-conf-bars i:nth-child(3) { height: 10px; }
.cop-conf--baixa .cop-conf-bars i:nth-child(1), .cop-conf--media .cop-conf-bars i:nth-child(-n+2), .cop-conf--alta .cop-conf-bars i { opacity: 1; }
.cop-source { color: var(--ink-50, #666); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.cop-source b { color: var(--ink-80, #222); font-weight: 600; }
.cop-source--none { white-space: normal; }
.cop-tone-tag { font-size: 10.5px; color: var(--cop); background: var(--cop-soft); padding: 1px 7px; border-radius: 999px; }
.cop-text { margin: 0; font-size: 13px; line-height: 1.5; color: var(--ink-80, #222); white-space: pre-line; overflow-wrap: anywhere; max-height: 140px; overflow-y: auto; }
.cop-caret { display: inline-block; width: 2px; height: 1em; margin-left: 1px; vertical-align: -2px; background: var(--cop); animation: cop-blink .8s steps(1) infinite; }

.cop-actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.cop-btn { display: inline-flex; align-items: center; gap: 6px; height: 28px; padding: 0 10px; border-radius: 7px; border: 1px solid var(--ink-10, #ddd); background: var(--white, #fff); color: var(--ink-60, #555); font: 600 12px/1 inherit; cursor: pointer; transition: background .14s, border-color .14s, transform .14s; }
.cop-btn:hover { border-color: var(--cop); color: var(--cop); }
.cop-btn--primary { background: var(--cop); border-color: var(--cop); color: #fff; }
.cop-btn--primary:hover { color: #fff; filter: brightness(1.08); transform: translateY(-1px); }
.cop-btn--ghost { border-color: transparent; background: transparent; }
.cop-btn kbd { font: 600 9.5px ui-monospace, Consolas, monospace; padding: 1px 4px; border-radius: 3px; background: rgba(0,0,0,.08); color: inherit; }
.cop-btn--primary kbd { background: rgba(255,255,255,.22); }
.cop-sep { width: 1px; height: 18px; background: var(--ink-10, #ddd); margin: 0 2px; }
.cop-tones { display: inline-flex; gap: 4px; flex-wrap: wrap; }
.cop-chip { display: inline-flex; align-items: center; gap: 5px; height: 24px; padding: 0 9px; border-radius: 999px; border: 1px solid var(--ink-10, #ddd); background: var(--white, #fff); color: var(--ink-60, #555); font: 600 11px/1 inherit; cursor: pointer; }
.cop-chip:hover:not(:disabled) { border-color: var(--cop); color: var(--cop); }
.cop-chip.is-on { background: var(--cop-soft); border-color: var(--cop); color: var(--cop); }
.cop-chip:disabled { opacity: .55; cursor: default; }
.cop-rate { display: inline-flex; gap: 2px; margin-left: auto; }
.cop-thumb { width: 28px; height: 26px; border-radius: 7px; border: 1px solid transparent; background: transparent; font-size: 13px; cursor: pointer; filter: grayscale(1); opacity: .6; transition: filter .14s, opacity .14s, transform .14s; }
.cop-thumb:hover, .cop-thumb.is-on { filter: none; opacity: 1; }
.cop-thumb.is-on { background: var(--cop-soft); border-color: rgba(124,58,237,.3); transform: scale(1.06); }
.cop-btn:focus-visible, .cop-chip:focus-visible, .cop-thumb:focus-visible, .cop-link:focus-visible { outline: 2px solid var(--cop); outline-offset: 2px; }
.cop-spin { width: 9px; height: 9px; border-radius: 50%; border: 2px solid rgba(124,58,237,.3); border-top-color: var(--cop); animation: cop-rot .7s linear infinite; }
.cop-problem { margin: 0; font-size: 11.5px; color: #b91c1c; }
.cop-hint { margin: 0; font-size: 11.5px; color: var(--ink-50, #666); }

/* Entradas */
.cop-card-enter-active { transition: opacity .2s ease, transform .2s ease; }
.cop-card-enter-from { opacity: 0; transform: translateY(6px); }
.cop-pop-enter-active, .cop-pop-leave-active { transition: opacity .25s ease, transform .25s ease; }
.cop-pop-enter-from { opacity: 0; transform: scale(.8); }
.cop-pop-leave-to { opacity: 0; }

@keyframes cop-pulse { 0% { box-shadow: 0 0 0 0 rgba(124,58,237,.35); } 100% { box-shadow: 0 0 0 9px rgba(124,58,237,0); } }
@keyframes cop-dot { 0%, 80%, 100% { transform: translateY(0); opacity: .45; } 40% { transform: translateY(-3px); opacity: 1; } }
@keyframes cop-blink { 50% { opacity: 0; } }
@keyframes cop-rot { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .is-reading .cop-av, .cop-dots i, .cop-caret, .cop-spin { animation: none; }
  .cop-card-enter-active, .cop-pop-enter-active, .cop-pop-leave-active { transition: none; }
}
</style>
